import equal from 'fast-deep-equal'
import { omitKeys } from 'lib/object/objectUtils'
import { convertTZ, getDateFromZonedDateString } from 'lib/datetime/dateUtils'
import { convertTimeObjectToMinutes, getTotalStopCount } from './flightUtils'
import { getScheduleChangeStopDateTime, parseScheduleChangeDateTime } from './scheduleChangePresentation'

type Flight = App.FlightScheduleChangeFlight
type Leg = App.FlightScheduleChangeLeg

export type ScheduleChangeField = 'departureAirport' | 'arrivalAirport' | 'departureDateTime' | 'arrivalDateTime' | 'flightNumber' | 'departureTerminal' | 'arrivalTerminal' | 'marketingAirline' | 'operatingAirline' | 'aircraftType' | 'cabinClass' | 'fareClass' | 'bookingClass' | 'fareFamilyName' | 'duration' | 'layover' | 'stopArrivalDateTime' | 'stopDepartureDateTime'

export type ScheduleChangeDifference =
  | { id: string, kind: 'stops', previous: Array<string | undefined>, updated: Array<string | undefined>, previousCount: number, updatedCount: number }
  | { id: string, kind: 'value', field: ScheduleChangeField, route: string, previous: string, updated: string }
  | { id: string, kind: 'details', field: 'baggage' | 'inclusions' | 'fareDetails' | 'fareRules', route: string }

/** Includes technical stops within each flight and connections between flights, in travel order. */
export function getScheduleChangeStopAirports(leg: Leg): Array<string | undefined> {
  return leg.flights.flatMap((flight, index) => {
    const next = leg.flights[index + 1]
    const arrival = flight.arrivalAirport
    const departure = next?.departureAirport
    // A ground transfer is one connection, with both airports shown in its label.
    const connection = arrival && departure ? arrival === departure ? arrival : `${arrival} / ${departure}` : undefined
    return [
      ...flight.hiddenStops.map(stop => stop.airportCode),
      ...(next ? [connection] : []),
    ]
  })
}

function routeOf(leg: App.FlightScheduleChangeEndpoints): string {
  return [leg.departureAirport, leg.arrivalAirport].filter(Boolean).join('–')
}

function durationInMinutes(duration: App.HoursMinutes | undefined): string | undefined {
  return duration ? String(convertTimeObjectToMinutes(duration)) : undefined
}

function flightDuration(flight: Flight): string | undefined {
  return flight.flightDuration === undefined ? durationInMinutes(flight.flightTime) : String(flight.flightDuration)
}

/** Prefer a unique captured identity, then an unambiguous departure/arrival route. */
function matchFlight(flight: Flight, previous: Array<Flight>, updated: Array<Flight>): Flight | undefined {
  if (flight.id && previous.filter(candidate => candidate.id === flight.id).length === 1) {
    const identified = updated.filter(candidate => candidate.id === flight.id)
    if (identified.length === 1) return identified[0]
  }
  if (!flight.departureAirport || !flight.arrivalAirport) return undefined
  const sameRoute = (candidate: Flight) => candidate.departureAirport === flight.departureAirport && candidate.arrivalAirport === flight.arrivalAirport
  const candidates = updated.filter(sameRoute)
  return previous.filter(sameRoute).length === 1 && candidates.length === 1 ? candidates[0] : undefined
}

function snapshotInstant(clock: ReturnType<typeof parseScheduleChangeDateTime>, zone: string): Date | undefined {
  const instant = getDateFromZonedDateString(clock.toISOString().slice(0, -1), zone)
  const restored = convertTZ(instant, zone)
  const expectedFields = [clock.year(), clock.month(), clock.date(), clock.hour(), clock.minute()]
  const restoredFields = [restored.getFullYear(), restored.getMonth(), restored.getDate(), restored.getHours(), restored.getMinutes()]
  // The shared converter rounds some fractional offsets. Reject conversions that alter the captured clock.
  return expectedFields.every((value, index) => value === restoredFields[index]) ? instant : undefined
}

function layoverMinutes(previous: Flight, next: Flight): string | undefined {
  if (!previous.arrivalAirport || previous.arrivalAirport !== next.departureAirport) return undefined
  const arrival = parseScheduleChangeDateTime(previous.arrivalDateTime)
  const departure = parseScheduleChangeDateTime(next.departureDateTime)
  // Both clocks belong to the same airport. Without its timezone an elapsed duration is uncertain.
  const zone = previous.arrivalTimezone
  if (!zone || zone !== next.departureTimezone || !arrival.isValid() || !departure.isValid()) return undefined
  try {
    const arrivalInstant = snapshotInstant(arrival, zone)
    const departureInstant = snapshotInstant(departure, zone)
    if (!arrivalInstant || !departureInstant) return undefined
    const minutes = (departureInstant.getTime() - arrivalInstant.getTime()) / 60000
    return Number.isFinite(minutes) && minutes >= 0 ? String(minutes) : undefined
  } catch {
    return undefined
  }
}

function sameDetails(previous: ReadonlyArray<unknown>, updated: ReadonlyArray<unknown>): boolean {
  return previous.length === updated.length && previous.every(value =>
    previous.filter(candidate => equal(candidate, value)).length === updated.filter(candidate => equal(candidate, value)).length)
}

export function getScheduleChangeDifferences(previous: Leg, updated: Leg): Array<ScheduleChangeDifference> {
  const differences: Array<ScheduleChangeDifference> = []
  if (!previous.flights.length || !updated.flights.length) return differences
  const oldStops = getScheduleChangeStopAirports(previous)
  const newStops = getScheduleChangeStopAirports(updated)
  const previousCount = getTotalStopCount(previous.flights)
  const updatedCount = getTotalStopCount(updated.flights)
  if (previousCount !== updatedCount || oldStops.every(Boolean) && newStops.every(Boolean) && !equal(oldStops, newStops)) {
    differences.push({ id: 'stops', kind: 'stops', previous: oldStops, updated: newStops, previousCount, updatedCount })
  }
  function addValue(field: ScheduleChangeField, oldValue: string | undefined, newValue: string | undefined, route: string, scope: string) {
    if (!oldValue?.trim() || !newValue?.trim() || oldValue === newValue) return
    if (field.endsWith('DateTime') &&
      (!parseScheduleChangeDateTime(oldValue).isValid() || !parseScheduleChangeDateTime(newValue).isValid())) return
    differences.push({ id: `${scope}-${field}`, kind: 'value', field, route, previous: oldValue, updated: newValue })
  }
  function addDetails(field: 'baggage' | 'inclusions' | 'fareDetails' | 'fareRules', oldValue: ReadonlyArray<unknown> | undefined, newValue: ReadonlyArray<unknown> | undefined, route: string, scope: string) {
    if (!oldValue || !newValue || sameDetails(oldValue, newValue)) return
    // The same captured condition can appear at both leg and segment level.
    if (differences.some(change => change.kind === 'details' && change.field === field && change.route === route)) return
    differences.push({ id: `${scope}-${field}`, kind: 'details', field, route })
  }
  const route = routeOf(updated)
  for (const field of ['departureAirport', 'arrivalAirport', 'departureDateTime', 'arrivalDateTime', 'fareFamilyName'] as const) {
    addValue(field, previous[field], updated[field], route, 'leg')
  }
  addValue('duration', durationInMinutes(previous.totalTravelDuration), durationInMinutes(updated.totalTravelDuration), route, 'leg')
  addDetails('inclusions', previous.inclusions, updated.inclusions, route, 'leg')
  previous.flights.forEach((flight, index) => {
    const proposed = matchFlight(flight, previous.flights, updated.flights)
    if (!proposed) return
    const scope = `flight-${index}`
    const flightRoute = routeOf(proposed)
    for (const field of ['flightNumber', 'departureTerminal', 'arrivalTerminal', 'aircraftType', 'cabinClass', 'fareClass', 'bookingClass'] as const) {
      addValue(field, flight[field], proposed[field], flightRoute, scope)
    }
    flight.hiddenStops.forEach((stop, stopIndex) => {
      if (!stop.airportCode || flight.hiddenStops.filter(candidate => candidate.airportCode === stop.airportCode).length !== 1) return
      const candidates = proposed.hiddenStops.filter(candidate => candidate.airportCode === stop.airportCode)
      if (candidates.length !== 1) return
      const stopScope = `${scope}-stop-${stopIndex}`
      addValue('stopArrivalDateTime', getScheduleChangeStopDateTime(stop, 'arrival'), getScheduleChangeStopDateTime(candidates[0], 'arrival'), stop.airportCode, stopScope)
      addValue('stopDepartureDateTime', getScheduleChangeStopDateTime(stop, 'departure'), getScheduleChangeStopDateTime(candidates[0], 'departure'), stop.airportCode, stopScope)
    })
    // Journey endpoints already appear once above; intermediate segment clocks still matter.
    if (index > 0 || flight.departureDateTime !== previous.departureDateTime || proposed.departureDateTime !== updated.departureDateTime) {
      addValue('departureDateTime', flight.departureDateTime, proposed.departureDateTime, flightRoute, scope)
    }
    if (index < previous.flights.length - 1 || flight.arrivalDateTime !== previous.arrivalDateTime || proposed.arrivalDateTime !== updated.arrivalDateTime) {
      addValue('arrivalDateTime', flight.arrivalDateTime, proposed.arrivalDateTime, flightRoute, scope)
    }
    for (const field of ['marketingAirline', 'operatingAirline'] as const) {
      const oldAirline = flight[field]
      const newAirline = proposed[field]
      if (oldAirline.code && oldAirline.code === newAirline.code) continue
      addValue(field, oldAirline.name ?? oldAirline.code, newAirline.name ?? newAirline.code, flightRoute, scope)
    }
    if (flightDuration(flight) !== durationInMinutes(previous.totalTravelDuration) || flightDuration(proposed) !== durationInMinutes(updated.totalTravelDuration)) {
      addValue('duration', flightDuration(flight), flightDuration(proposed), flightRoute, scope)
    }
    addDetails('baggage', flight.baggageAllowances?.map(bag => omitKeys(['source'], bag)), proposed.baggageAllowances?.map(bag => omitKeys(['source'], bag)), flightRoute, scope)
    addDetails('inclusions', flight.inclusions, proposed.inclusions, flightRoute, scope)
    addDetails('fareDetails', flight.fareDetails, proposed.fareDetails, flightRoute, scope)
    addDetails('fareRules', flight.fareRules, proposed.fareRules, flightRoute, scope)
    const next = previous.flights[index + 1]
    const newIndex = updated.flights.indexOf(proposed)
    const newNext = updated.flights[newIndex + 1]
    if (next && newNext && flight.arrivalAirport === proposed.arrivalAirport && matchFlight(next, previous.flights, updated.flights) === newNext) {
      addValue('layover', layoverMinutes(flight, next), layoverMinutes(proposed, newNext), flight.arrivalAirport ?? flightRoute, scope)
    }
  })
  return differences
}
